import React, { useRef } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
} from '@roger/ui-components';
import { AlertTriangle, FileText, Loader2, Upload } from 'lucide-react';

import { readInvoiceFaxNumberPortsInvoiceReadingPost as readInvoice } from '../../../services/api/sdk.gen';
import type { InvoiceReadingResponse } from '../../../services/api/types.gen';
import { getHttpStatus } from '../../../utils/httpErrorUtils';
import PdfFileButton from './PdfFileButton';
import { describeError, logFailure, type Notify, pdfProblem } from './shared';

/** An invoice picked before Start port, with what Roger read from it. */
export interface HeldInvoice {
  file: File;
  reading: InvoiceReadingResponse | null;
  isReading: boolean;
}

/**
 * The optional invoice, picked after the check and sent with Start port.
 * A read that finds nothing, or fails, still keeps the file: it goes as the
 * invoice and the person types the details.
 */
const InvoicePicker: React.FC<{
  invoice: HeldInvoice | null;
  disabled: boolean;
  onChange: (invoice: HeldInvoice | null) => void;
  showError: Notify;
}> = ({ invoice, disabled, onChange, showError }) => {
  // Only the newest pick may land, so a slow read never replaces a newer file.
  const pickRef = useRef(0);

  const handleFile = async (file: File) => {
    const problem = pdfProblem(file);
    if (problem) {
      showError(problem);
      return;
    }
    const pick = ++pickRef.current;
    onChange({ file, reading: null, isReading: true });
    try {
      const { data } = await readInvoice({
        body: { file },
        throwOnError: true,
      });
      if (pick === pickRef.current) {
        onChange({ file, reading: data, isReading: false });
      }
    } catch (error) {
      if (pick !== pickRef.current) return;
      logFailure('Failed to read port invoice', error);
      const status = getHttpStatus(error);
      if (status !== undefined && status < 500) {
        onChange(null);
        showError(describeError(error, 'Unable to read this file.'));
        return;
      }
      onChange({ file, reading: null, isReading: false });
    }
  };

  const remove = () => {
    pickRef.current += 1;
    onChange(null);
  };

  const found = invoice?.reading?.read ? invoice.reading.fields_found : 0;

  return (
    <section className='space-y-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <h3 className='text-base font-semibold'>Recent carrier invoice</h3>
        <Badge variant='neutral' className='whitespace-nowrap'>
          Optional
        </Badge>
      </div>
      <p className='max-w-2xl text-sm text-muted-foreground'>
        Roger fills in your account details from it for you to check. No
        invoice? Your signed authorization letter is sent in its place.
      </p>

      {!invoice && (
        <PdfFileButton
          inputLabel='Invoice to read'
          disabled={disabled}
          onFile={file => void handleFile(file)}
        >
          <Upload className='mr-2 h-4 w-4' />
          Upload invoice
        </PdfFileButton>
      )}

      {invoice?.isReading && (
        <div className='flex items-center gap-3 rounded-md border p-3 text-sm'>
          <Loader2 className='h-4 w-4 shrink-0 animate-spin text-muted-foreground' />
          <span className='min-w-0 break-words'>
            Reading {invoice.file.name}
          </span>
        </div>
      )}

      {invoice && !invoice.isReading && (
        <div className='flex flex-col gap-3 rounded-md border p-3 text-sm sm:flex-row sm:items-center'>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
            <div className='flex min-w-0 flex-col'>
              <span className='break-words font-medium'>
                {invoice.file.name}
              </span>
              {found > 0 && (
                <span className='text-xs text-muted-foreground'>
                  {found} detail{found === 1 ? '' : 's'} found
                </span>
              )}
            </div>
          </div>
          <div className='flex flex-wrap gap-2'>
            <PdfFileButton
              inputLabel='Replacement invoice to read'
              disabled={disabled}
              onFile={file => void handleFile(file)}
            >
              Replace
            </PdfFileButton>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              disabled={disabled}
              onClick={remove}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {invoice && !invoice.isReading && found === 0 && (
        <Alert className='border-amber-300 bg-amber-50 text-amber-800'>
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>No details found in {invoice.file.name}</AlertTitle>
          <AlertDescription>
            It is still sent as your invoice. Enter the details on the next
            step.
          </AlertDescription>
        </Alert>
      )}

      <p className='text-xs text-muted-foreground'>PDF, 10 MB or smaller.</p>
    </section>
  );
};

export default InvoicePicker;
